import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  AttributeGroupResponse,
  AttributeGroupUpdate,
  useCreateAttributeGroup,
  useUpdateAttributeGroup,
} from '@/entities/attributes';
import type { PredicateInputDto, PredicateValue } from '@/entities/predicate';
import { useHasPermission } from '@/entities/profile';
import { useFormCodeGeneration } from '@/shared/hooks';
import { useAppNavigate } from '@/shared/lib';
import { AttributeGroupFormValues, GroupAttributeValue, toGroupAttributeValue } from './types';
import { useAttributeGroupSchema } from './validation';
import { useFormGuardActions } from '@/entities/formGuard';

/** Предикат группы в теле запроса: пустое поле — у группы предиката нет */
const toPredicateDto = (predicate: PredicateValue): PredicateInputDto | null => {
  const dsl = predicate.dsl?.trim();

  return dsl ? { dsl, dslHuman: predicate.dslHuman?.trim() || undefined } : null;
};

/**
 * Условие правили — сравнение с исходным, а не признак изменённости поля: правка состава
 * пересчитывает признаки всей формы, и у нетронутого условия признак становится непустым объектом
 */
const isPredicateEdited = (value: PredicateValue, initial?: Pick<Partial<PredicateValue>, 'dsl' | 'dslHuman'>) =>
  (value.dsl ?? '') !== (initial?.dsl ?? '') || (value.dslHuman ?? '') !== (initial?.dslHuman ?? '');

/** Состав группы в теле запроса: строка формы несёт подписи, на сервер уходят код и структура */
const toAttributesDto = (attributes: GroupAttributeValue[]) =>
  attributes.map(({ code, parentStructCode }) => ({ code, parentStructCode }));

type Params = {
  initial?: AttributeGroupResponse;
  onSuccess?: () => void;
};

export const useAttributeGroupForm = (params: Params) => {
  const { initial, onSuccess } = params;
  const navigate = useAppNavigate();
  const allowAdmin = useHasPermission('ADMIN_ATTRIBUTE_GROUP');
  const schema = useAttributeGroupSchema();
  const { resetFormGuard } = useFormGuardActions();

  // Право на редактирование управляет активностью элементов, а не выбором вида карточки
  const isEdit = !!initial;
  const isDisabled = !allowAdmin || initial?.code === 'system';

  const defaults: AttributeGroupFormValues = useMemo(
    () => ({
      name: initial?.name ?? '',
      code: initial?.code ?? '',
      description: initial?.description ?? '',
      attributes: initial?.attributes?.map(toGroupAttributeValue) ?? [],
      predicate: { dsl: initial?.predicate?.dsl ?? '', dslHuman: initial?.predicate?.dslHuman ?? '' },
    }),
    [initial],
  );

  const form = useForm<AttributeGroupFormValues>({
    values: defaults,
    resolver: zodResolver(schema),
    // Перечитанная группа (например, после переименования в заголовке) не затирает несохранённые правки карточки
    resetOptions: { keepDirtyValues: true },
  });

  // Use code generation hook
  const { isCodeLocked, isGeneratingCode, handleNameBlur } = useFormCodeGeneration({
    getName: () => form.getValues('name'),
    getCode: () => form.getValues('code'),
    setCode: (code) => form.setValue('code', code),
    entityType: 'attributeGroup',
    isEdit,
  });

  const { mutateAsync: createGroup, isPending: isCreating } = useCreateAttributeGroup();
  const { mutateAsync: updateGroup, isPending: isUpdating } = useUpdateAttributeGroup();
  const isLoading = isCreating || isUpdating;

  const onSubmit = form.handleSubmit(async (values) => {
    if (isEdit) {
      if (initial?.code === 'system') {
        return;
      }
      const body: AttributeGroupUpdate = {
        name: values.name,
        description: values.description,
        attributes: toAttributesDto(values.attributes),
      };
      // Без правки поля предикат не передаётся: привязанный к группе не переписывается
      if (isPredicateEdited(values.predicate, form.formState.defaultValues?.predicate)) {
        body.predicate = toPredicateDto(values.predicate);
      }
      await updateGroup({ code: initial!.code, body });
      // Отправленное становится сохранённым: карточка перестаёт считать себя изменённой
      form.reset(values);
    } else {
      const created = await createGroup({
        code: values.code,
        name: values.name,
        description: values.description,
        attributes: toAttributesDto(values.attributes),
        predicate: toPredicateDto(values.predicate),
      });
      form.reset(values);
      resetFormGuard();
      navigate('/attribute-groups/:id', { id: created.code }, { replace: true });
    }

    resetFormGuard();
    onSuccess?.();
  });

  const handleTitleSave = async (value: string) => {
    form.setValue('name', value);

    if (isEdit && value !== initial?.name && initial?.code !== 'system') {
      await updateGroup({
        code: initial!.code,
        body: {
          name: value,
          description: initial!.description,
        },
      });
    }
  };

  return {
    form,
    isEdit,
    isLoading,
    isDisabled,
    onSubmit,
    handleTitleSave,
    handleNameBlur,
    isCodeLocked,
    isGeneratingCode,
  } as const;
};
